import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Plus, X, FileText, Upload, CheckCircle2, AlertCircle, Ticket, CalendarClock, Banknote, Search, ShieldCheck, HandCoins, LayoutList, Briefcase } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import BoletoAvulsoModal from './BoletoAvulsoModal';
import BoletoAvulsoGranModal from './BoletoAvulsoGranModal';
import ConfirmarDuplicataModal from './ConfirmarDuplicataModal';
import { ehErroDuplicataFatura, extrairUcEMesDoErro } from '../../src/lib/duplicataFatura';
import AgendarLembreteModal from './components/AgendarLembreteModal';
import CriarParcelamentoModal from './CriarParcelamentoModal';
import ConsultarCobrancaModal from './ConsultarCobrancaModal';
import { podeAuditarPagamentos } from './AuditoriaPagamentosView';
import { podeEmitirBoletoForaParceria } from '../../supabase/functions/_shared/permissaoEmissaoParceira.ts';

interface FaturaFABProps {
    onAdd: () => void;
    onRefresh?: () => void;
}

const FaturaFAB: React.FC<FaturaFABProps> = ({ onAdd, onRefresh }) => {
    const navigate = useNavigate();
    const { profile } = useAuth();
    const canAuditarPagamentos = podeAuditarPagamentos(profile);
    // Painel de adesão ao boleto único (#5579): fila de trabalho diária da operação, não
    // auditoria — mesma faixa de papéis que a rota libera, sem exigir permissão extra.
    const canVerAdesaoBoletoUnico = ['adm', 'gerente', 'analista'].includes(profile?.role || '');
    // Boleto avulso da Gran (cliente próprio dela, fora da parceria): a mesma regra que
    // a edge asaas-cobranca aplica — adm, Faturamento/Financeiro interno ou quem tem a
    // permissão de cobrança. Mostrar para outro perfil só renderia 403.
    const canEmitirAvulsoGran = podeEmitirBoletoForaParceria(profile);
    // O borderô de agrupamento (.7z com várias faturas, #6208) saiu daqui: é importado na tela
    // de Agrupamentos de Faturamento. Quem tem acesso a ela (adm, gerente, analista) ainda pode
    // escolher o .7z aqui por hábito — e recebe o aviso de onde ele foi parar.
    const canImportarBordero = ['adm', 'gerente', 'analista'].includes(profile?.role || '');
    const [isOpen, setIsOpen] = useState(false);
    const backdropMouseDownRef = useRef(false);

    // PDF Import Modal State
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isAvulsoModalOpen, setIsAvulsoModalOpen] = useState(false);
    const [isAvulsoGranModalOpen, setIsAvulsoGranModalOpen] = useState(false);
    const [isLembreteModalOpen, setIsLembreteModalOpen] = useState(false);
    const [isParcelamentoModalOpen, setIsParcelamentoModalOpen] = useState(false);
    const [isConsultarCobrancaModalOpen, setIsConsultarCobrancaModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
    // Segunda fatura do mesmo mês: quem insere é o n8n, então a confirmação vira uma
    // autorização gravada antes do envio. `null` = modal fechado.
    const [duplicata, setDuplicata] = useState<{ uc?: string; mes?: string } | null>(null);
    const [duplicataAutorizada, setDuplicataAutorizada] = useState<string | null>(null);

    // Fetch Webhook URL
    useEffect(() => {
        const fetchWebhookUrl = async () => {
            try {
                const { data } = await supabase
                    .from('system_settings')
                    .select('value')
                    .eq('key', 'pdf_invoice_webhook_url')
                    .single();
                if (data?.value) {
                    setWebhookUrl(data.value);
                }
            } catch (error) {
                console.error('Error fetching webhook URL:', error);
            }
        };

        if (isImportModalOpen && !webhookUrl) {
            fetchWebhookUrl();
        }
    }, [isImportModalOpen, webhookUrl]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            // O .7z é o borderô de agrupamento (várias faturas): tem importação própria, que
            // separa uma fatura por página antes de mandar ao robô, na tela de Agrupamentos.
            if (/\.7z$/i.test(file.name) && canImportarBordero) {
                setSelectedFile(null);
                setUploadError('O .7z é o borderô de agrupamento. Ele é importado em Financeiro → Agrupamentos de Faturamento, no botão "Importar borderô de agrupamento".');
                return;
            }
            if (file.type === 'application/pdf') {
                setSelectedFile(file);
                setUploadError(null);
            } else {
                setUploadError('Por favor, selecione um arquivo PDF válido.');
                setSelectedFile(null);
            }
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setUploadError('Selecione um arquivo PDF para importar.');
            return;
        }

        if (!webhookUrl) {
            setUploadError('URL do Webhook não configurada. Verifique as configurações de APIs.');
            return;
        }

        setIsUploading(true);
        setUploadError(null);
        setUploadSuccess(false);

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const response = await fetch(webhookUrl, {
                method: 'POST',
                body: formData,
            });

            const responseData = await response.json().catch(() => ({})) as Record<string, any>;

            if (!response.ok || responseData?.success === false) {
                throw new Error(responseData?.error || 'Falha ao enviar arquivo para o sistema (n8n).');
            }

            setUploadSuccess(true);
            setTimeout(() => {
                setIsImportModalOpen(false);
                setUploadSuccess(false);
                setSelectedFile(null);
                setIsOpen(false);
                onRefresh?.();
            }, 3000);
        } catch (error: any) {
            console.error('Upload error:', error);
            // O n8n repassa o texto do banco quando a UC já tem fatura no mês: em vez do
            // erro cru, pergunta se é a segunda fatura legítima.
            if (ehErroDuplicataFatura(error)) {
                const doErro = extrairUcEMesDoErro(error);
                setDuplicata({ uc: doErro?.uc, mes: doErro?.mes });
            } else {
                setUploadError(error.message || 'Erro inesperado ao enviar arquivo.');
            }
        } finally {
            setIsUploading(false);
        }
    };

    const resetModal = () => {
        setIsImportModalOpen(false);
        setSelectedFile(null);
        setUploadError(null);
        setUploadSuccess(false);
    };

    return createPortal(
        <>
            {/* No celular: alinhado ao botão da Vick (bottom-5), só o ícone, e a lista de
                ações rola se não couber na altura da tela. */}
            <div className="fixed bottom-5 right-4 md:bottom-8 md:right-8 z-40 flex flex-col items-end gap-3">
                {isOpen && (
                    <div className="flex flex-col items-end gap-3 mb-2 animate-fade-in-up max-h-[70vh] overflow-y-auto p-1 md:max-h-none md:overflow-visible md:p-0">
                        <button
                            onClick={() => {
                                onAdd();
                                setIsOpen(false);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold rounded-lg shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all border border-gray-100 dark:border-gray-700"
                        >
                            <span className="text-sm">Adicionar manualmente</span>
                            <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary dark:text-primary-light">
                                <FileText size={16} />
                            </div>
                        </button>

                        <button
                            onClick={() => {
                                setIsImportModalOpen(true);
                                setIsOpen(false);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold rounded-lg shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all border border-gray-100 dark:border-gray-700"
                        >
                            <span className="text-sm">Importar fatura em PDF</span>
                            <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary dark:text-primary-light">
                                <Upload size={16} />
                            </div>
                        </button>

                        <button
                            onClick={() => {
                                setIsAvulsoModalOpen(true);
                                setIsOpen(false);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold rounded-lg shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all border border-gray-100 dark:border-gray-700"
                        >
                            <span className="text-sm">Gerar cobrança avulsa</span>
                            <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary dark:text-primary-light">
                                <Ticket size={16} />
                            </div>
                        </button>

                        {canEmitirAvulsoGran && (
                            <button
                                onClick={() => {
                                    setIsAvulsoGranModalOpen(true);
                                    setIsOpen(false);
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold rounded-lg shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all border border-gray-100 dark:border-gray-700"
                            >
                                <span className="text-sm">Boleto avulso da Gran</span>
                                <div className="w-8 h-8 rounded-full bg-secondary/15 flex items-center justify-center text-secondary">
                                    <Ticket size={16} />
                                </div>
                            </button>
                        )}

                        <button
                            onClick={() => {
                                setIsLembreteModalOpen(true);
                                setIsOpen(false);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold rounded-lg shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all border border-gray-100 dark:border-gray-700"
                        >
                            <span className="text-sm">Agendar lembrete</span>
                            <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary dark:text-primary-light">
                                <CalendarClock size={16} />
                            </div>
                        </button>

                        <button
                            onClick={() => {
                                setIsParcelamentoModalOpen(true);
                                setIsOpen(false);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold rounded-lg shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all border border-gray-100 dark:border-gray-700"
                        >
                            <span className="text-sm">Criar parcelamento</span>
                            <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary dark:text-primary-light">
                                <Banknote size={16} />
                            </div>
                        </button>

                        <button
                            onClick={() => {
                                setIsConsultarCobrancaModalOpen(true);
                                setIsOpen(false);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold rounded-lg shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all border border-gray-100 dark:border-gray-700"
                        >
                            <span className="text-sm">Consultar cobrança</span>
                            <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary dark:text-primary-light">
                                <Search size={16} />
                            </div>
                        </button>

                        {canVerAdesaoBoletoUnico && (
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate('/financeiro/adesao-boleto-unico');
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold rounded-lg shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all border border-gray-100 dark:border-gray-700"
                            >
                                <span className="text-sm">Adesão ao boleto único</span>
                                <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary dark:text-primary-light">
                                    <LayoutList size={16} />
                                </div>
                            </button>
                        )}

                        {canVerAdesaoBoletoUnico && (
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate('/financeiro/carteira-faturamento');
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold rounded-lg shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all border border-gray-100 dark:border-gray-700"
                            >
                                <span className="text-sm">Carteira de faturamento</span>
                                <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary dark:text-primary-light">
                                    <Briefcase size={16} />
                                </div>
                            </button>
                        )}

                        {canAuditarPagamentos && (
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate('/financeiro/auditoria-pagamentos');
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold rounded-lg shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all border border-gray-100 dark:border-gray-700"
                            >
                                <span className="text-sm">Auditar pagamentos</span>
                                <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary dark:text-primary-light">
                                    <ShieldCheck size={16} />
                                </div>
                            </button>
                        )}

                        {canAuditarPagamentos && (
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate('/financeiro/repasse-equatorial-manual');
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold rounded-lg shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all border border-gray-100 dark:border-gray-700"
                            >
                                <span className="text-sm">Repasse Equatorial — manual</span>
                                <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary dark:text-primary-light">
                                    <HandCoins size={16} />
                                </div>
                            </button>
                        )}
                    </div>
                )}

                <button
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label="Nova ação"
                    className={`group flex items-center gap-2 p-3 md:pl-4 md:pr-2 md:py-2 rounded-full shadow-xl transition-all duration-300 ${isOpen ? 'bg-primary-dark dark:bg-gray-700 text-white' : 'bg-primary text-white hover:bg-primary-dark'
                        }`}
                >
                    <span className="hidden md:inline font-bold text-sm">Nova ação</span>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-300 ${isOpen ? 'bg-gray-700 dark:bg-gray-600 rotate-45' : 'bg-white/20'
                        }`}>
                        <Plus size={20} />
                    </div>
                </button>
            </div>

            {/* Import PDF Modal */}
            {isImportModalOpen && (
                <div
                    className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in"
                    onMouseDown={e => { backdropMouseDownRef.current = e.target === e.currentTarget; }}
                    onClick={() => { if (backdropMouseDownRef.current && !isUploading) resetModal(); backdropMouseDownRef.current = false; }}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-scale-in"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
                            <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                <FileText className="text-primary" />
                                Importar Fatura (PDF)
                            </h2>
                            {!isUploading && (
                                <button onClick={resetModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                    <X size={24} />
                                </button>
                            )}
                        </div>

                        <div className="p-6">
                            {isUploading ? (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="120" height="120">
                                        <defs>
                                            <style>
                                                {`
                                                    .spin-forward {
                                                        transform-origin: 50px 50px;
                                                        animation: rotate-forward 1.5s cubic-bezier(0.4, 0.0, 0.2, 1) infinite;
                                                    }
                                                    .spin-reverse {
                                                        transform-origin: 50px 50px;
                                                        animation: rotate-reverse 2s linear infinite;
                                                    }
                                                    .pulse-energy {
                                                        transform-origin: 50px 50px;
                                                        animation: pulse 1.5s ease-in-out infinite alternate;
                                                    }
                                                    
                                                    @keyframes rotate-forward {
                                                        0% { transform: rotate(0deg); }
                                                        100% { transform: rotate(360deg); }
                                                    }
                                                    @keyframes rotate-reverse {
                                                        0% { transform: rotate(360deg); }
                                                        100% { transform: rotate(0deg); }
                                                    }
                                                    @keyframes pulse {
                                                        0% { transform: scale(0.85); opacity: 0.8; }
                                                        100% { transform: scale(1.05); opacity: 1; }
                                                    }
                                                `}
                                            </style>
                                        </defs>
                                        <circle className="spin-forward" cx="50" cy="50" r="40" fill="none" stroke="#3A5A40" strokeWidth="5" strokeDasharray="160 80" strokeLinecap="round" />
                                        <circle className="spin-reverse" cx="50" cy="50" r="28" fill="none" stroke="#E09F3E" strokeWidth="4" strokeDasharray="60 100" strokeLinecap="round" />
                                        <path className="pulse-energy" d="M51 32 L39 53 L49 53 L47 68 L61 46 L51 46 Z" fill="#3A5A40" />
                                    </svg>
                                    <p className="mt-6 text-lg font-medium text-gray-700 dark:text-gray-200">Enviando fatura...</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-2 max-w-[80%]">
                                        Por favor, aguarde enquanto o arquivo é processado.
                                    </p>
                                </div>
                            ) : uploadSuccess ? (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4 text-green-600 dark:text-green-400">
                                        <CheckCircle2 size={40} />
                                    </div>
                                    <p className="text-lg font-bold text-gray-800 dark:text-gray-100">Fatura enviada!</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">O processamento será concluído em breve.</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors relative cursor-pointer">
                                        <input
                                            type="file"
                                            accept={canImportarBordero ? 'application/pdf,.7z' : 'application/pdf'}
                                            onChange={handleFileChange}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                        <div className="p-4 bg-primary/10 dark:bg-primary/20 rounded-full text-primary dark:text-primary-light mb-4">
                                            <Upload size={32} />
                                        </div>
                                        {selectedFile ? (
                                            <div className="text-center">
                                                <p className="font-medium text-gray-800 dark:text-gray-200">{selectedFile.name}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="text-center">
                                                <p className="font-medium text-gray-700 dark:text-gray-300">Clique para selecionar um arquivo</p>
                                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                    {canImportarBordero ? 'PDF de uma fatura. O borderô de agrupamento (.7z) é importado em Agrupamentos de Faturamento.' : 'Apenas arquivos PDF'}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {uploadError && (
                                        <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2 text-sm border border-red-100 dark:border-red-800">
                                            <AlertCircle size={16} className="shrink-0" />
                                            <p>{uploadError}</p>
                                        </div>
                                    )}

                                    {duplicataAutorizada && (
                                        <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 rounded-lg flex items-center gap-2 text-sm border border-emerald-100 dark:border-emerald-800">
                                            <CheckCircle2 size={16} className="shrink-0" />
                                            <p>Duplicata autorizada para {duplicataAutorizada}. Envie o PDF: ele será aceito mesmo já havendo fatura deste mês.</p>
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        onClick={() => setDuplicata({})}
                                        className="w-full text-left text-xs text-gray-500 dark:text-gray-400 underline underline-offset-2 hover:text-primary"
                                    >
                                        É a segunda fatura do mesmo mês para esta UC? Autorize antes de enviar.
                                    </button>

                                    {!webhookUrl && !uploadError && (
                                        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-500 rounded-lg flex items-center gap-2 text-sm border border-yellow-200 dark:border-yellow-800/30">
                                            <AlertCircle size={16} className="shrink-0" />
                                            <p>A URL do webhook de importação não está configurada.</p>
                                        </div>
                                    )}

                                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                                        <button
                                            onClick={resetModal}
                                            className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={handleUpload}
                                            disabled={!selectedFile || !webhookUrl}
                                            className="flex items-center gap-2 px-6 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Upload size={18} />
                                            Enviar
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <BoletoAvulsoModal
                isOpen={isAvulsoModalOpen}
                onClose={() => setIsAvulsoModalOpen(false)}
                onSuccess={() => {
                    // Force refresh if needed, usually onAdd handle this via props
                }}
            />

            <BoletoAvulsoGranModal
                isOpen={isAvulsoGranModalOpen}
                onClose={() => setIsAvulsoGranModalOpen(false)}
                onSuccess={() => onRefresh?.()}
            />

            <AgendarLembreteModal 
                isOpen={isLembreteModalOpen}
                onClose={() => setIsLembreteModalOpen(false)}
            />

            <CriarParcelamentoModal
                isOpen={isParcelamentoModalOpen}
                onClose={() => setIsParcelamentoModalOpen(false)}
                onSuccess={() => {
                    onRefresh?.();
                }}
            />

            <ConsultarCobrancaModal
                isOpen={isConsultarCobrancaModalOpen}
                onClose={() => setIsConsultarCobrancaModalOpen(false)}
            />

            {duplicata && (
                <ConfirmarDuplicataModal
                    ucInicial={duplicata.uc}
                    mesInicial={duplicata.mes}
                    origem="upload_pdf"
                    onClose={() => setDuplicata(null)}
                    onAutorizado={({ uc, mes }) => {
                        const veioDoErro = !!duplicata.uc;
                        setDuplicata(null);
                        setDuplicataAutorizada(`UC ${uc} · ${mes}`);
                        setUploadError(null);
                        // Quando a recusa veio do envio, o arquivo ainda está aqui: reenvia sozinho.
                        if (veioDoErro && selectedFile) handleUpload();
                    }}
                />
            )}
        </>,
        document.body
    );
};

export default FaturaFAB;
